/**
 * URL history adapter for AJAX-loaded SeeMe overlays.
 *
 * `SeeMe.overlay` owns rendering and dismissal. This adapter gives lazy
 * overlays the same refresh/back/forward contract as rfSheet:
 *
 *   window.rfModal.open('/items/42/modal/')
 *   window.rfModal.open('/items/42/modal/', {
 *       type: 'sheet',
 *       requestParams: {overlay: 'sheet'},
 *       history: 'replace',
 *   })
 *
 * The canonical URL is stored in `?modal=`. Request-only parameters let a
 * responsive endpoint return different overlay chrome without changing the
 * URL someone copies or refreshes.
 */

(function () {
    'use strict';

    var active = null;

    function relativeSameOriginUrl(value) {
        if (typeof value !== 'string' || !value || value.charAt(0) !== '/' || value.indexOf('//') === 0) {
            return null;
        }

        try {
            var parsed = new URL(value, window.location.origin);
            if (parsed.origin !== window.location.origin) return null;
            return parsed.pathname + parsed.search;
        } catch (error) {
            return null;
        }
    }

    function normalizedOptions(options) {
        options = options || {};
        return {
            type: options.type === 'sheet' ? 'sheet' : 'modal',
            overlayClass: options.overlayClass || '',
            title: options.title || '',
            requestParams: options.requestParams || null,
        };
    }

    function requestUrl(url, options) {
        var parsed = new URL(url, window.location.origin);
        var params = options.requestParams;
        if (params) {
            Object.keys(params).forEach(function (key) {
                var value = params[key];
                if (value === null || typeof value === 'undefined') {
                    parsed.searchParams.delete(key);
                } else {
                    parsed.searchParams.set(key, value);
                }
            });
        }
        return parsed.pathname + parsed.search;
    }

    function currentPageUrl(params) {
        var query = params.toString().replace(/%2F/gi, '/');
        return window.location.pathname + (query ? '?' + query : '') + window.location.hash;
    }

    function writeModalParam(url, mode) {
        var params = new URLSearchParams(window.location.search);
        if (url) {
            params.set('modal', url);
        } else {
            params.delete('modal');
        }

        // Preserve existing state, especially HTMX's navigation snapshot.
        var state = history.state ? Object.assign({}, history.state) : {};
        var fn = mode === 'push' ? history.pushState : history.replaceState;
        fn.call(history, state, '', currentPageUrl(params));
    }

    function removeDynamicNode(el) {
        if (el && el.hasAttribute('data-overlay-dynamic') && el.parentNode) {
            el.parentNode.removeChild(el);
        }
    }

    function closeActive(updateHistory) {
        if (!active) {
            if (updateHistory) writeModalParam(null, 'replace');
            return;
        }

        var closing = active;
        active = null;
        closing.syncing = !updateHistory;
        window.SeeMe.overlay.close(closing.el);
        // SeeMe normally removes lazy overlays after transitionend. Remove the
        // retired node now when replacing/navigating so late responses cannot
        // leave duplicate record ids in the document.
        if (!updateHistory) removeDynamicNode(closing.el);
    }

    function open(url, options) {
        var canonicalUrl = relativeSameOriginUrl(url);
        if (!canonicalUrl || !window.SeeMe || !window.SeeMe.overlay) return null;

        var historyMode = options && options.history;
        var opts = normalizedOptions(options);

        closeActive(false);
        document.querySelectorAll('[data-rf-modal-history]').forEach(removeDynamicNode);

        var el = window.SeeMe.overlay.load(requestUrl(canonicalUrl, opts), opts.type, {
            overlayClass: opts.overlayClass,
            title: opts.title,
        });
        el.setAttribute('data-rf-modal-history', '');
        var entry = {el: el, url: canonicalUrl, options: opts, syncing: false};
        active = entry;

        el.addEventListener('seeme:overlay-close', function () {
            if (active === entry) active = null;
            if (!entry.syncing) writeModalParam(null, 'replace');
        });
        el.addEventListener('seeme:overlay-loaded', function () {
            // SeeMe adopts whole-overlay responses by replacing every
            // attribute on the placeholder, so restore our ownership marker.
            el.setAttribute('data-rf-modal-history', '');
            if (active !== entry) removeDynamicNode(el);
        });

        if (historyMode !== 'none') {
            writeModalParam(canonicalUrl, historyMode === 'replace' ? 'replace' : 'push');
        }
        return el;
    }

    function close() {
        closeActive(true);
    }

    function refresh() {
        if (!active) return null;
        var entry = active;
        closeActive(false);
        return open(entry.url, Object.assign({}, entry.options, {history: 'none'}));
    }

    function syncFromLocation() {
        var params = new URLSearchParams(window.location.search);
        var modalUrl = relativeSameOriginUrl(params.get('modal'));
        if (!modalUrl) {
            closeActive(false);
            if (params.has('modal')) writeModalParam(null, 'replace');
            return;
        }

        // History stores the canonical URL only. Consumers can refine the
        // initial overlay type through this bubbling event before it loads.
        var detail = {url: modalUrl, options: {history: 'none'}};
        document.dispatchEvent(new CustomEvent('rfmodal:resolve', {detail: detail}));
        open(detail.url, detail.options);
    }

    window.addEventListener('popstate', syncFromLocation);

    // Overlays live under <body>, outside #page-content. Close them before a
    // boosted full-page swap so no modal survives onto the destination page.
    document.body.addEventListener('htmx:beforeSwap', function (event) {
        var target = event.detail && event.detail.target;
        if (target && target.id === 'page-content') closeActive(false);
    });
    document.body.addEventListener('htmx:afterSettle', function (event) {
        var target = event.detail && event.detail.target;
        if (!target || target.id !== 'page-content') return;
        // HTMX has installed the destination URL by settle time. Defer once so
        // route-specific listeners from the swapped page's footer scripts have
        // also had a chance to register before resolving its modal URL.
        window.setTimeout(syncFromLocation, 0);
    });

    window.rfModal = {
        open: open,
        close: close,
        refresh: refresh,
        active: function () { return active && active.el; },
    };

    // Let later bundle imports register route-specific `rfmodal:resolve`
    // handlers before resolving a direct-link URL on the initial page load.
    window.setTimeout(syncFromLocation, 0);
})();
